import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check, X, Search as SearchIcon } from "lucide-react";

/**
 * People multi-select — like MultiSelectDropdown but options are
 * `{id, name, email, color}` objects instead of plain strings. Used on the
 * job form to pick multiple installers (crew jobs) and multiple assistants
 * (prep-work staff). Value is a string[] of user ids.
 *
 * Props:
 *   label      : label shown above the trigger
 *   options    : Array<{id, name, email, color, role}>
 *   value      : string[] of selected user ids
 *   onChange   : (nextIds: string[], nextPeople: Array) => void
 *   testId     : data-testid on the trigger button
 *   placeholder: text shown when nothing is selected
 *   emptyText  : text shown inside the panel when the roster has no matches
 */
export default function PeopleMultiSelect({
  label,
  options = [],
  value = [],
  onChange,
  testId,
  placeholder = "Select people…",
  emptyText = "No one available",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const rootRef = useRef(null);
  const selectedSet = new Set(value || []);
  const selectedPeople = options.filter((o) => selectedSet.has(o.id));

  useEffect(() => {
    if (!open) {
      setQuery("");
      return;
    }
    // Autofocus the search box when the panel opens so staff with 10+ people
    // on the roster can start filtering immediately.
    const t = setTimeout(() => searchRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const toggle = (id) => {
    const next = selectedSet.has(id)
      ? value.filter((v) => v !== id)
      : [...value, id];
    const nextPeople = options.filter((o) => next.includes(o.id));
    onChange(next, nextPeople);
  };

  const remove = (id, e) => {
    e.stopPropagation();
    const next = value.filter((v) => v !== id);
    const nextPeople = options.filter((o) => next.includes(o.id));
    onChange(next, nextPeople);
  };

  return (
    <div ref={rootRef} className="relative">
      {label && <label className="text-label block mb-1.5">{label}</label>}
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((v) => !v)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-white/30 flex items-center justify-between gap-2 min-h-[42px]"
      >
        <div className="flex flex-wrap gap-1.5 flex-1 text-left">
          {selectedPeople.length === 0 && (
            <span className="text-slate-500">{placeholder}</span>
          )}
          {selectedPeople.map((p) => (
            <span
              key={p.id}
              data-testid={`${testId}-chip-${p.id}`}
              className="inline-flex items-center gap-1.5 pill pill-muted text-[11px]"
            >
              {p.color && (
                <span
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: p.color }}
                />
              )}
              {p.name || p.email}
              <span
                onClick={(e) => remove(p.id, e)}
                className="text-slate-500 hover:text-white cursor-pointer"
                data-testid={`${testId}-remove-${p.id}`}
              >
                <X className="w-3 h-3" />
              </span>
            </span>
          ))}
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid={`${testId}-panel`}
          className="absolute z-50 mt-1.5 w-full bg-neutral-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden"
        >
          {/* Search bar — shown when roster is more than a handful so admins
              with large crews can jump straight to a person. */}
          {options.length >= 6 && (
            <div className="p-2 border-b border-white/5">
              <div className="relative">
                <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setOpen(false);
                    e.stopPropagation();
                  }}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`${testId}-search`}
                  placeholder={`Search ${options.length} people…`}
                  className="w-full bg-black/40 border border-white/10 rounded-lg pl-7 pr-2.5 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
                />
              </div>
            </div>
          )}
          <div className="max-h-72 overflow-y-auto py-1">
            {options.length === 0 && (
              <div className="px-3 py-4 text-sm text-slate-500 italic">{emptyText}</div>
            )}
            {(() => {
              const needle = query.trim().toLowerCase();
              const filtered = needle
                ? options.filter((p) => {
                    const hay = `${p.name || ""} ${p.email || ""} ${p.role || ""}`.toLowerCase();
                    return hay.includes(needle);
                  })
                : options;
              if (options.length > 0 && filtered.length === 0) {
                return (
                  <div
                    data-testid={`${testId}-empty`}
                    className="px-3 py-4 text-sm text-slate-500 italic text-center"
                  >
                    No matches for &ldquo;{query}&rdquo;
                  </div>
                );
              }
              return filtered.map((p) => {
              const active = selectedSet.has(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`${testId}-option-${p.id}`}
                  onClick={() => toggle(p.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition ${
                    active ? "bg-white/[0.06] text-white" : "text-slate-300 hover:bg-white/[0.04]"
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded-md border flex items-center justify-center shrink-0 ${
                      active ? "bg-white/90 border-white/90" : "border-white/20"
                    }`}
                  >
                    {active && <Check className="w-3 h-3 text-black" />}
                  </span>
                  {p.color && (
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: p.color }}
                    />
                  )}
                  <span className="flex-1 truncate">
                    {p.name || p.email}
                    {p.email && p.name && (
                      <span className="text-slate-500 text-xs ml-1.5">({p.email})</span>
                    )}
                  </span>
                </button>
              );
            });
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
